import { action, computed, makeObservable } from 'mobx';
import castArray from 'lodash/castArray';
import every from 'lodash/every';
import isNil from 'lodash/isNil';

import type { AvatarUnits, Identifiable } from 'chaika-frontend-types/frontend-cms';

import { Validation } from '@utils/Validation';
import { AnyObject } from '@store/types';

export interface InitParams {
    name: string;
    isRequired?: boolean;
    validation?: ValidationType | ValidationType[];
    format?(value: ValueType): ValueType;
    onUpdateHandler?(value: ValueType): void;
}

export enum ValidationType {
    Name = 'isName',
    NameEng = 'isEngName',
    Word = 'isWord',
    Date = 'isDate',
    Birthday = 'isBirthday',
    FormattedDate = 'isFormattedDate',
    NumberString = 'isNumberString',
    Phone = 'isPhone',
    PhoneString = 'isPhoneString',
    notEmpty = 'notEmpty',
    Email = 'isEmail',
    Snils = 'isSnils',
    Inn = 'isInn',
    Url = 'isURL',
    StringArray = 'isStringArray',
    StringDate = 'isStringDate',
    Nickname = 'isNickName',
    AvatarUnits = 'isAvatarUnits',
}

export type ValueType = string | number | Date | boolean | null | AvatarUnits | any[];

interface FieldParams {
    name: string;
    isRequired: boolean;
}

const validationFunction = {
    [ValidationType.Name]: Validation.isName,
    [ValidationType.NameEng]: Validation.isEngName,
    [ValidationType.Word]: Validation.isWord,
    [ValidationType.Date]: Validation.isDate,
    [ValidationType.Birthday]: Validation.isBirthday,
    [ValidationType.FormattedDate]: Validation.isFormattedDate,
    [ValidationType.NumberString]: Validation.isNumberString,
    [ValidationType.Phone]: Validation.isPhone,
    [ValidationType.PhoneString]: Validation.isPhoneString,
    [ValidationType.notEmpty]: Validation.notEmpty,
    [ValidationType.Email]: Validation.isEmail,
    [ValidationType.Snils]: Validation.isSnils,
    [ValidationType.Inn]: Validation.isInn,
    [ValidationType.Url]: Validation.isUrl,
    [ValidationType.StringArray]: Validation.isStringArray,
    [ValidationType.StringDate]: Validation.isStringDate,
    [ValidationType.Nickname]: Validation.isNickname,
    [ValidationType.AvatarUnits]: Validation.isAvatarUnits,
};

export abstract class Form {
    protected storeName!: string;

    protected validation: { [fieldName: string]: ValidationType[] } = {};

    protected format: { [fieldName: string]: (value: ValueType) => ValueType } = {};

    protected onUpdateHandlers: { [fieldName: string]: (value: ValueType) => void } = {};

    protected fields: { [fieldName: string]: FieldParams } = {};

    constructor() {
        makeObservable(this);
    }

    // Computed вызывает баг в reservation form.
    // Могут быть проблемы и в других местах.
    // Лучше использовать обычную ф-ю
    @computed
    public get isFormValid(): boolean {
        return every(this.fields, validStatus => this.isFieldValid(validStatus.name));
    }

    @action.bound
    public getIsFormValid(): boolean {
        return every(this.fields, validStatus => this.isFieldValid(validStatus.name));
    }

    @action
    public setFieldValue(fieldName: string, value: ValueType) {
        const format = (this.format as AnyObject)[fieldName];
        this.store[fieldName] = format ? format(value) : value;
        this.onUpdateHandler(fieldName);
    }

    @action
    public addArrayValue(fieldName: string, value: any) {
        const format = (this.format as AnyObject)[fieldName];
        const formattedValue = format ? (format([value]) as AnyObject)?.[0] : value;

        this.store[fieldName] = this.store[fieldName].concat(formattedValue);
        this.onUpdateHandler(fieldName);
    }

    @action
    public removeArrayValue(fieldName: string, value: ValueType) {
        this.store[fieldName] = (this.store[fieldName] as ValueType[]).filter(fieldValue => fieldValue !== value);
        this.onUpdateHandler(fieldName);
    }

    @action
    public removeIdentifiableArrayValue(fieldName: string, value: Identifiable<ValueType>) {
        this.store[fieldName] = (this.store[fieldName] as Identifiable<ValueType>[]).filter(
            ({ id }) => id !== value.id,
        );
        this.onUpdateHandler(fieldName);
    }

    @action
    public updateIdentifiableArrayValue(fieldName: string, value: Identifiable<ValueType>) {
        const format = (this.format as AnyObject)[fieldName];
        const formattedValue = format ? format([value])?.[0] : value;

        this.store[fieldName] = (this.store[fieldName] as Identifiable<ValueType>[]).map(params =>
            params.id === formattedValue.id ? { ...params, ...formattedValue } : params,
        );
        this.onUpdateHandler(fieldName);
    }

    public getFieldValue<T = ValueType>(fieldName: string): T {
        return this.store[fieldName];
    }

    public isFieldRequired(fieldName: string): boolean {
        return this.fields[fieldName].isRequired;
    }

    public isFieldValid(fieldName: string): boolean {
        return this.validation[fieldName] ? this.validateField(fieldName, this.validation[fieldName]) : true;
    }

    protected get store(): any {
        return this.storeName ? (this as AnyObject)[this.storeName] : this;
    }

    @action
    protected init() {
        const params = this.getInitParams();

        params.forEach(({ name, isRequired = false, validation, format, onUpdateHandler }) => {
            // @ts-ignore
            this.validation[name] = validation && castArray(validation);
            // @ts-ignore
            this.format[name] = format;
            // @ts-ignore
            this.onUpdateHandlers[name] = onUpdateHandler;

            this.fields[name] = {
                name,
                isRequired,
            };
        });
    }

    protected abstract getInitParams(): InitParams[];

    protected toNumber(props: { min?: number; max?: number } = {}) {
        return (value: string) => {
            let res: number | string = '';

            if (Validation.isNumberString(value)) {
                res = Number(value);

                if (props.min) {
                    res = res > props.min ? res : props.min;
                }

                if (props.max) {
                    res = res < props.max ? res : props.max;
                }
            }

            return res;
        };
    }

    protected onlyNumbers(value: string) {
        return value ? value.replace(/\D/gm, '') : '';
    }

    private validateField(fieldName: string, validationTypes: ValidationType[]): boolean {
        const value = this.getFieldValue(fieldName);

        const isRequered = this.fields[fieldName]?.isRequired;

        let isValid = true;
        if (isRequered || (!!validationTypes && !(isNil(value) || value === '' || (value as any[]).length === 0))) {
            isValid = validationTypes
                .map(validationType => validationFunction[validationType](value as any))
                .every(value => value);
        }

        return isValid;
    }

    private onUpdateHandler(fieldName: string): void {
        const onUpdateHandler = this.onUpdateHandlers[fieldName];
        if (onUpdateHandler) {
            const value = this.store[fieldName];
            onUpdateHandler(value);
        }
    }
}
